import { v4 as uuidv4 } from "uuid";
import { useState } from "react";
import { useToast } from "@packsize/toast";
import { useIntl } from "react-intl";

import { emptyGuid } from "@/constants";
import { usePrintConfig, usePrintImages, useSubmitTestJob, useTechnicalJobs } from "@/hooks/print-image";
import type { PrintImage, X5wPrintServiceMachine } from "@/schemas";

import { X5wPrintServiceTemplate } from "./x5-w-print-service.template";

export interface X5wPrintServiceProps {
	readonly selectedMachine: X5wPrintServiceMachine;
}

type CartonMedia = {
	Dimensions: {
		Length: string | null;
		Width: string | null;
		Height: string | null;
	};
	Style: string;
	Attributes: {
		X1: string;
		name: string;
		quantity: string;
	};
};

type PrintMedia = {
	dimensions: {
		Length: number;
		Width: number;
		Height: number;
	};
	thickness: number;
	style: null;
	brand: null;
	cost: number;
	attributes: Record<string, unknown>;
};

type Print = {
	placement: {
		X: number;
		Y: number;
	};
	media: PrintMedia;
	rotation: number;
	layer: number;
	cost: number;
	value: string;
	requiredTimeToPack: string;
	attributes: Record<string, string | boolean>;
};

type Carton = {
	additionalDimensions: Record<string, unknown>;
	attributes: Record<string, unknown>;
	contents: unknown[];
	cost: number;
	documents: unknown[];
	errors: unknown[];
	fillers: unknown[];
	id: string;
	labels: unknown[];
	media?: CartonMedia;
	prints?: Print[];
};

export const X5wPrintService = ({ selectedMachine }: X5wPrintServiceProps) => {
	const intl = useIntl();
	const { toastSuccess } = useToast();
	const { colorProfiles, colorModes, colorants } = usePrintConfig(
		selectedMachine.iotEdgeDeviceId,
		selectedMachine.iotHubName,
	);
	const { technicalJobs } = useTechnicalJobs(selectedMachine.iotHubName, selectedMachine.iotEdgeDeviceId);
	const { submitJob } = useSubmitTestJob();

	const [imageSource, setImageSource] = useState<string | null>(null);
	const [colorWise, setColorWise] = useState<boolean | null>(null);
	const [colorProfile, setColorProfile] = useState<string | null>(null);
	const [colorMode, setColorMode] = useState<string | null>(null);
	const [colorant, setColorant] = useState<string | null>(null);
	const [packNetSource, setPackNetSource] = useState<string | null>(null);
	const [sftpSource, setSFTPSource] = useState<string | null>(null);
	const [fierySource, setFierySource] = useState<string | null>(null);
	const [cartonLength, setCartonLength] = useState<string | null>(null);
	const [cartonWidth, setCartonWidth] = useState<string | null>(null);
	const [cartonHeight, setCartonHeight] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [isSmartScaling, setIsSmartScaling] = useState<boolean>(false);
	const [fssFilepath, setFssFilepath] = useState<string>("");
	const [dotFilepath, setDotFilepath] = useState<string>("");
	const [shippingFilepath, setShippingFilepath] = useState<string>("");
	const [barcodeFilepath, setBarcodeFilepath] = useState<string>("");
	const [boxId, setBoxId] = useState<number>(0);

	const { printImages: filteredPrintImages, pdfOptions: filteredPdfOptions } = usePrintImages(
		cartonLength ? parseInt(cartonLength) : null,
		cartonWidth ? parseInt(cartonWidth) : null,
		cartonHeight ? parseInt(cartonHeight) : null,
	);

	const handlePackNetSourceChange = (printImageId: string) => {
		const printImage: PrintImage | undefined = filteredPrintImages.find((image) => image.id === printImageId);
		if (printImage) {
			setPackNetSource(printImage.alias ?? null);
		}
	};

	const createAndSubmitTestJob = async (): Promise<void> => {
		setLoading(true);
		const carton: Carton = {
			additionalDimensions: {},
			attributes: {},
			contents: [],
			cost: 0,
			documents: [],
			errors: [],
			fillers: [],
			id: "",
			labels: [],
		};
		carton.media = {
			Dimensions: {
				Length: cartonLength,
				Width: cartonWidth,
				Height: cartonHeight,
			},
			Style: selectedMachine.settings.designId.toString(),
			Attributes: {
				X1: "2.5",
				name: "Service Test Carton",
				quantity: "1",
			},
		};
		carton.prints = [
			{
				placement: {
					X: 0.0,
					Y: 0.0,
				},
				media: {
					dimensions: {
						Length: 0,
						Width: 0,
						Height: 0,
					},
					thickness: 0.0,
					style: null,
					brand: null,
					cost: 0.0,
					attributes: {},
				},
				rotation: 0.0,
				layer: 1,
				cost: 0.0,
				value:
					imageSource === "Packnet"
						? (packNetSource ?? "")
						: imageSource === "SFTP"
							? isSmartScaling
								? fssFilepath
								: (sftpSource ?? "")
							: "technicaljob:" + (fierySource ?? ""),
				requiredTimeToPack: "0001-01-01T00:00:00Z",
				attributes: {
					colorWiseOff: colorWise?.toString() ?? "false",
					ColorMode: colorMode ?? "",
					ColorProfile: colorProfile ?? "",
					Colorant: colorant ?? "",
					"@@NoValidatePrintImage": imageSource === "Packnet" ? "False" : "True",
					smartScaling: isSmartScaling.toString(),
					lpnImagePath: barcodeFilepath,
					dotPath: dotFilepath,
					shippingPath: shippingFilepath,
					boxId: boxId.toString(),
				},
			},
		];
		const solutionToSend = {
			userName: null,
			tenantId: selectedMachine.tenantId,
			batchId: undefined,
			alias: "Service Job-" + imageSource,
			licensePlateNumber: "",
			cartons: [carton],
			classification: null,
			contentWeight: 0,
			createByDate: "0001-01-01T00:00:00+00:00",
			documents: [],
			errors: [],
			estimatedTimeToPack: "00:00:00",
			estimatedTotalCost: 0,
			fillers: [],
			groupIdDate: "0001-01-01T00:00:00+00:00",
			id: uuidv4(),
			importDate: "0001-01-01T00:00:00+00:00",
			internalUniqueId: emptyGuid,
			isExpanded: false,
			priority: 0,
			releasedForProductionDate: "0001-01-01T00:00:00+00:00",
			options: { prioritizeProductionBy: "MaterialUsage", maxFailedProductionRetries: 2147483647 },
		};
		try {
			await submitJob({ solution: solutionToSend, machineId: selectedMachine.id });
			toastSuccess({
				message: intl.formatMessage(
					{
						id: "{alias} sent to queue",
						defaultMessage: "{alias} sent to queue",
					},
					{ alias: solutionToSend.alias },
				),
			});
		} catch (error) {
			console.error("Failed to submit job:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<X5wPrintServiceTemplate
			selectedMachine={selectedMachine}
			colorProfiles={colorProfiles}
			colorModes={colorModes}
			colorants={colorants}
			technicalJobs={technicalJobs}
			selectPdfOptions={filteredPdfOptions}
			imageSource={imageSource}
			colorMode={colorMode}
			cartonLength={cartonLength}
			cartonWidth={cartonWidth}
			cartonHeight={cartonHeight}
			loading={loading}
			isSmartScaling={isSmartScaling}
			fssFilepath={fssFilepath}
			dotFilepath={dotFilepath}
			shippingFilepath={shippingFilepath}
			barcodeFilepath={barcodeFilepath}
			boxId={boxId}
			onImageSourceChange={setImageSource}
			onColorWiseChange={setColorWise}
			onColorProfileChange={setColorProfile}
			onColorModeChange={setColorMode}
			onColorantChange={setColorant}
			onPackNetSourceChange={handlePackNetSourceChange}
			onSftpSourceChange={setSFTPSource}
			onFierySourceChange={setFierySource}
			onCartonLengthChange={setCartonLength}
			onCartonWidthChange={setCartonWidth}
			onCartonHeightChange={setCartonHeight}
			onSmartScalingChange={setIsSmartScaling}
			onFssFilepathChange={setFssFilepath}
			onDotFilepathChange={setDotFilepath}
			onShippingFilepathChange={setShippingFilepath}
			onBarcodeFilepathChange={setBarcodeFilepath}
			onBoxIdChange={(value) => setBoxId(parseInt(value))}
			onSubmitTestJob={createAndSubmitTestJob}
		/>
	);
};
